import IconButton from '@/components/Buttons/IconButton';
import Loader from '@/components/Loaders/Loader';
import { toast } from '@/components/toast';
import CustomTooltip from '@/components/ToolTip/CustomTooltip';
import { studioDomain } from '@/environment';
import DeleteIcon from '@/images/Bin.svg';
import CheckCircle from '@/images/CheckCircle.svg';
import CheckCircleGreen from '@/images/GreenCheckCircle.svg';
import InfoCircleGrey from '@/images/InfoCircleGrey.svg';
import LinkMinimalistic from '@/images/LinkMinimalistic.svg';
import { useRefetchAgents, useUpdateAgent } from '@/pages/agents/api';
import { useAnalytics } from '@/services/analytics';
import { useAuth } from '@/services/auth';
import { AgentShareActiveTab, useClientKey, usePatchClientKey } from '@/state/agent';
import { useAgentEditorStore } from '@/state/root';
import theme from '@/theme';
import { H2 } from '@blueprintjs/core';
import { IAgent, IAgentKey } from '@d-id/types';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, InputAdornment, TextField } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrandingToolTip } from './BrandingToolTip';
import { EmbedSnippet } from './EmbedSnippet';
import { generateCode } from './generateCode';
import { ModeTabs } from './ModeTabs';
import { PositionTabs } from './PositionTabs';
import Preview from './SharePreview';
import { EmbedMode, EmbedPosition } from './types';

enum DomainDeletionState {
    NotStarted = 'NotStarted',
    ToConfirm = 'ToConfirm',
    Confirmed = 'Confirm',
    Deleted = 'Deleted',
}

interface Props {
    agent?: IAgent;
    open: boolean;
    onClose: () => void;
    errorHandler?: (error: any) => Promise<void>;
}

const SuccessDiv = styled.div`
    display: flex;
    padding: 10px 16px;
    align-items: center;
    border-radius: 8px;
    gap: 8px;
    background: #dff8e9;
    border: 1px solid #29cc6a;
    margin-top: 8px;
`;

const SuccessTextCredits = styled.span`
    color: ${theme.colors.secondary.main};
    font-family: Manrope, sans-serif;
    font-size: 1.6rem;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: -0.32px;
`;

const IconSuccessCredits = styled.img`
    display: flex;
    width: 24px;
    height: 24px;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
`;

const StyledDialog = styled(Dialog)`
    .MuiDialog-paper {
        border-radius: 1.25rem;
        display: flex;
        width: 1100px;
        max-width: 1100px;
    }
`;

const ModalContent = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px;
`;

const TitleH2 = styled(H2)`
    color: ${theme.colors.secondary.main};
    font-size: 22px !important;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: -0.48px;
    height: fit-content;
    margin: 0;
`;

const IconTitle = styled.div`
    display: flex;
    align-items: center;
    flex-direction: 'flex-row';
    gap: 8px;
`;

const SubParagraphTitle = styled.span`
    color: ${theme.colors.secondary.main};
    font-size: 1.6rem;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
`;

const IconContainer = styled.img`
    margin-right: auto;
    margin-left: auto;
    display: flex;
`;

const IconForTitle = styled.img`
    margin-right: 6px;
    display: flex;
`;

const ToolTipContainer = styled.span`
    color: ${theme.colors.black[900]};
    display: flex;
    align-items: center;
    margin-top: 4px;
    font-size: 1.4rem;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    flex-direction: 'flex-row';
    gap: 8px;
`;

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    flex-shrink: 0;
`;

const StyledBox = styled('div')<{ hasError: boolean }>`
    border: 1px solid ${props => (props.hasError ? theme.colors.error.main : theme.colors.black[400])};
    border-radius: 0.8rem;
    padding: 0px 8px;
    margin-top: 20px;
`;

const TextFieldsContainer = styled('div')`
    display: flex;
    flex-direction: column;
`;

const StyledBoxPartionWithPadding = styled(Box)`
    width: 100%;
`;

const ErrorText = styled('p')`
    color: red;
    margin-top: 8px;
    font-size: 14px;
`;

const StyledTextField = styled(TextField)`
    border-radius: 8px;
    .MuiInput-underline:after {
        border-bottom: 1px solid ${theme.colors.black[400]} !important;
    }

    .MuiInput-underline:before {
        border-bottom: 1px solid ${theme.colors.black[400]} !important;
    }

    &:hover {
        .MuiInput-underline:hover:not(.Mui-disabled):before {
            border-bottom: 1px solid ${theme.colors.black[400]} !important;
        }
    }

    &:focus-within {
        .MuiInput-underline:hover:not(.Mui-disabled):before {
            border-bottom: 1px solid ${theme.colors.black[400]} !important;
        }
    }

    .MuiInput-input {
        font-family: 'Manrope', sans-serif !important;
        padding-top: 10px;
        padding-bottom: 10px;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 24px; /* 150% */
        letter-spacing: -0.32px;
    }

    & > div {
        min-height: 48px;
    }
`;

const AddDomainButton = styled(Button)`
    min-width: 87px;
    height: 34px;
    padding: 7px 8px;
    font-size: 14px;
    border-radius: 8px;
    font-family: 'Manrope', sans-serif !important;
    text-transform: none;
    box-shadow: none;

    :hover {
        box-shadow: none;
        background: ${theme.colors.secondary[800]};
    }
`;

const ConfirmButton = styled(Button)`
    height: 34px;
    margin-top: 10px;
    margin-left: 5px;
    color: ${theme.colors.white};
    font-size: 14px;
    border-radius: 0.5rem;
    background: ${theme.colors.error.main};
    font-family: 'Manrope', sans-serif !important;
    text-transform: none;
    font-weight: 600;
    padding: 0px;

    &:hover {
        background: ${theme.colors.error.hover};
    }
`;

const CancelButton = styled(Button)`
    height: 34px;
    margin-top: 10px;
    margin-right: 5px;
    border: 1px solid ${theme.colors.black[400]};
    color: ${theme.colors.black[900]};
    font-size: 14px;
    border-radius: 0.5rem;
    background: ${theme.colors.white};
    font-family: 'Mulish', sans-serif !important;
    text-transform: capitalize;
    font-weight: 600;
    padding: 0px;

    &:hover {
        background: ${theme.colors.white};
    }
`;

const StyledIconButton = styled(IconButton)`
    margin-top: 10px;
`;

const StyledIconContainer = styled.div`
    white-space: nowrap;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    vertical-align: middle;
    text-decoration: none;
    text-align: center;
    flex: 0 0 auto;
    border-radius: 50%;
    margin-top: 10px;
`;
const StyledIconInputs = styled.img`
    text-align: center;
    font-size: 1.5rem;
    box-sizing: inherit;
    display: flex;
`;

const StyledLoaderInput = styled(Loader)`
    width: 16px;
    height: 16px;
`;

const ButtonDiv = styled('div')`
    margin-bottom: 10px;
`;

const CloseButton = styled(IconButton)`
    position: absolute;
    top: 21px;
    right: 20px;
`;

const CopyCodeButton = styled(Button)`
    background: ${theme.colors.secondary.main};
    color: white;
    font-size: 18px;
    width: 100%;
    transition: transform 0.3s ease-in-out;
    &:hover {
        background: ${theme.colors.secondary[800]};
    }
    font-weight: 600;
    border-radius: 8px;
    margin-top: 18px;
    text-transform: none;
`;

const PageDescription = styled.p`
    color: #5c5c5c;
    font-family: Manrope;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 140%;
    padding-bottom: 10px;
    letter-spacing: 0.15px;
`;

const TwoColumn = styled.div`
    display: flex;
    flex-direction: row;
    flex: 1;
    gap: 8px;
`;

const Container = styled.div`
    display: flex;
    gap: 12px;
    @media (max-width: 768px) {
        flex-direction: column;
    }
`;

const RightPreviewPanel = styled.div`
    flex: 3;
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 28px;

    @media (max-width: 768px) {
        display: none;
    }
`;

const LeftSettingsPanel = styled.div`
    flex: 2;
    gap: 8px;
    display: flex;
    flex-direction: column;
    @media (max-width: 768px) {
        flex: 1 1 100%;
    }
`;

export const AgentShareModalV2 = observer(({ agent, open, onClose, errorHandler }: Props) => {
    const agentEditorStore = useAgentEditorStore();

    const mixpanel = useAnalytics();
    const { isAnonymous } = useAuth();
    const [agentCodeCopied, setAgentCodeCopied] = useState(false);
    const { data: clientKey } = useClientKey({ isAnonymous });
    const [domains, setDomains] = useState<string[]>([]);
    const [newDomain, setNewDomain] = useState('');

    const [errors, setErrors] = useState<('domain-validation' | 'min-one-domain')[]>([]);
    const [domainDeleteStatus, setDomainDeleteStatus] = useState<DomainDeletionState[]>([]);
    const [isAddingNewDomain, setIsAddingNewDomain] = useState(false);

    const { mutateAsync: patchClientKey, isLoading: isUpdating } = usePatchClientKey();
    const { mutateAsync: updateAgentMutation, isLoading } = useUpdateAgent(errorHandler);
    const { refetchAgents } = useRefetchAgents();

    const domainsString = useMemo(() => domains.join(','), [domains]);
    const [mode, setMode] = useState<EmbedMode>('fabio');
    const [position, setPosition] = useState<EmbedPosition>('right');

    const setHasValidationError = (hasError: boolean) => {
        setErrors(prev => {
            return hasError ? [...prev, 'domain-validation'] : prev.filter(error => error !== 'domain-validation');
        });
    };

    const setHasMinOneDomainError = (hasError: boolean) => {
        setErrors(prev => {
            return hasError ? [...prev, 'min-one-domain'] : prev.filter(error => error !== 'min-one-domain');
        });
    };

    useEffect(() => {
        if (agentCodeCopied) setAgentCodeCopied(false);
    }, [domainsString, mode, position, agent?.id]);

    const handleUpdateAgent = async () => {
        if (!agent?.id || (agent.access !== 'private' && agent.access !== 'unlisted')) return;
        const updateAgentAccess = {
            access: 'unlisted',
            embed: true,
        };
        await updateAgentMutation({ agentId: agent.id, update: updateAgentAccess });
        await refetchAgents();
    };

    useEffect(() => {
        if (open) {
            mixpanel?.track(`agent-share-agent`, { event: 'view', agentId: agent?.id });
        }
    }, [open, agent?.id]);

    useEffect(() => {
        if (open && clientKey) {
            setDomains(subtractStudioDomains(clientKey.allowed_domains ?? []));
        }
    }, [clientKey, open]);

    useEffect(() => {
        if (open) {
            if (!agent?.embed) handleUpdateAgent();
        }
    }, [open]);

    const handleAddDomain = async () => {
        if (isValidURL(newDomain)) {
            setIsAddingNewDomain(true);
            if (domains.indexOf(newDomain) !== -1) {
                toast.error({ title: `Cannot add duplicated domain` });
                return;
            }
            const updatedDomains = [...domains, newDomain];
            const update = {
                client_key: clientKey?.client_key,
                allowed_domains: [studioDomain, ...updatedDomains],
            } as IAgentKey;
            await patchClientKey(update);

            setDomains(updatedDomains);
            setNewDomain('');
            setHasValidationError(false);
            setHasMinOneDomainError(false);
            setIsAddingNewDomain(false);
        } else {
            setHasValidationError(true);
            setIsAddingNewDomain(false);
        }
    };

    const handleDeleteDomain = async (index: number) => {
        const updatedDomains = [...domains];
        updatedDomains.splice(index, 1);
        setDomains(updatedDomains);
        const update = {
            client_key: clientKey?.client_key,
            allowed_domains: [studioDomain, ...updatedDomains],
        } as IAgentKey;
        await patchClientKey(update);
        const updatedDomainDeleteStatus = [...domainDeleteStatus];
        updatedDomainDeleteStatus.splice(index, 1);
        setDomainDeleteStatus(updatedDomainDeleteStatus);
    };

    const handleDomainStateChange = (index: number, status: DomainDeletionState) => {
        const updatedDomainDeleteStatus = [...domainDeleteStatus];
        updatedDomainDeleteStatus[index] = status;
        setDomainDeleteStatus(updatedDomainDeleteStatus);

        if (updatedDomainDeleteStatus.length === 1 && updatedDomainDeleteStatus.includes(DomainDeletionState.ToConfirm)) {
            return setHasMinOneDomainError(true);
        }

        setHasMinOneDomainError(false);
    };

    const isValidURL = (url: string) => {
        try {
            new URL(url);
            return true;
        } catch (error) {
            return false;
        }
    };

    const subtractStudioDomains = (domains: string[]) => {
        return domains.filter(item => item !== studioDomain);
    };

    const handleOnClose = () => {
        setDomains([]);
        setHasValidationError(false);
        onClose();
        setDomainDeleteStatus([]);
        agentEditorStore.setShareActiveTab(AgentShareActiveTab.Embed);
    };

    const embedCode = useMemo(() => {
        return generateCode({
            mode,
            clientKey: clientKey?.client_key,
            position,
            orientation: 'horizontal',
            agentId: agent?.id,
            version: '2',
        });
    }, [mode, agent?.id, position, clientKey?.client_key]);

    const handleClick = useCallback(() => {
        window.navigator.clipboard.writeText(embedCode);
        setAgentCodeCopied(true);
    }, [embedCode]);

    const showBrandingToolTip = false; //user?.planGroup !== PlanGroup.ENTERPRISE;

    if (isLoading) {
        return null;
    }

    return (
        <StyledDialog open={open} onClose={handleOnClose}>
            <ModalContent>
                <Container>
                    <LeftSettingsPanel>
                        <TitleH2>Embed your agent</TitleH2>
                        <CloseButton ariaLabel="close" onClick={handleOnClose}>
                            <CloseIcon color="secondary" fontSize="large" />
                        </CloseButton>
                        <ModeTabs value={mode} setValue={setMode} />
                        <PageDescription>Make your Agent float throughout your website.</PageDescription>
                        {showBrandingToolTip && (
                            <ContentContainer>
                                <StyledBoxPartionWithPadding hidden={agentEditorStore.shareActiveTab !== AgentShareActiveTab.Embed}>
                                    <ToolTipContainer>
                                        <BrandingToolTip />
                                        Hide D-ID branding
                                    </ToolTipContainer>
                                </StyledBoxPartionWithPadding>
                            </ContentContainer>
                        )}
                        <ContentContainer>
                            <StyledBoxPartionWithPadding hidden={agentEditorStore.shareActiveTab !== AgentShareActiveTab.Embed}>
                                <IconTitle>
                                    <SubParagraphTitle>Add your domain name(s)</SubParagraphTitle>
                                    <CustomTooltip
                                        fitContent
                                        tooltipStyles={{ fontSize: '14px', padding: '8px' }}
                                        title="Add the domains where you want your Agent to be embedded">
                                        <IconForTitle src={InfoCircleGrey} />
                                    </CustomTooltip>
                                </IconTitle>
                                <StyledBox hasError={errors.includes('domain-validation')}>
                                    <TextFieldsContainer>
                                        {domains.map((domain, index) => (
                                            <StyledTextField
                                                variant="standard"
                                                fullWidth
                                                type="text"
                                                value={domain}
                                                key={domain}
                                                InputProps={{
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <StyledIconInputs src={LinkMinimalistic} />
                                                        </InputAdornment>
                                                    ),
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <ButtonDiv>
                                                                {domainDeleteStatus[index] !== DomainDeletionState.ToConfirm && (
                                                                    <StyledIconButton
                                                                        onClick={() =>
                                                                            handleDomainStateChange(index, DomainDeletionState.ToConfirm)
                                                                        }
                                                                        ariaLabel={''}>
                                                                        <IconContainer src={DeleteIcon} />
                                                                    </StyledIconButton>
                                                                )}
                                                                {!isUpdating &&
                                                                    domainDeleteStatus[index] !== DomainDeletionState.ToConfirm && (
                                                                        <StyledIconContainer>
                                                                            <StyledIconInputs
                                                                                src={CheckCircle}
                                                                                width="16px"
                                                                                height="16px"
                                                                            />
                                                                        </StyledIconContainer>
                                                                    )}
                                                                {isUpdating && index === domains.length - 1 && isAddingNewDomain && (
                                                                    <StyledIconButton ariaLabel="">
                                                                        <StyledLoaderInput color="black" />
                                                                    </StyledIconButton>
                                                                )}
                                                                {domainDeleteStatus[index] === DomainDeletionState.ToConfirm && (
                                                                    <>
                                                                        <CancelButton
                                                                            onClick={() =>
                                                                                handleDomainStateChange(
                                                                                    index,
                                                                                    DomainDeletionState.NotStarted
                                                                                )
                                                                            }>
                                                                            Cancel
                                                                        </CancelButton>
                                                                        <ConfirmButton onClick={() => handleDeleteDomain(index)}>
                                                                            Confirm
                                                                        </ConfirmButton>
                                                                    </>
                                                                )}
                                                            </ButtonDiv>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                        ))}
                                        <StyledTextField
                                            variant="standard"
                                            placeholder={!domains.length ? 'You haven’t added any domains yet' : 'https://yourdomain.com'}
                                            fullWidth
                                            value={newDomain}
                                            onChange={e => {
                                                setNewDomain(e.target.value);
                                                setHasValidationError(false);
                                            }}
                                            disabled={isUpdating}
                                            onKeyDown={e => {
                                                if (e.key === 'Enter') {
                                                    handleAddDomain();
                                                }
                                            }}
                                            InputProps={{
                                                disableUnderline: true,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <StyledIconInputs src={LinkMinimalistic} style={{ opacity: 0.25 }} />
                                                    </InputAdornment>
                                                ),
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <AddDomainButton variant="contained" color="secondary" onClick={handleAddDomain}>
                                                            Add URL
                                                        </AddDomainButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </TextFieldsContainer>
                                </StyledBox>

                                {/* taking error from -1 position allows us to display latest error */}
                                {errors.at(-1) === 'domain-validation' && (
                                    <ErrorText>Enter a valid domain (e.g., https://example.com). localhost isn't allowed.</ErrorText>
                                )}

                                {errors.at(-1) === 'min-one-domain' && (
                                    <ErrorText>At least one domain is required to activate the agent.</ErrorText>
                                )}

                                {mode === 'fabio' && (
                                    <TwoColumn>
                                        <PositionTabs value={position} setValue={setPosition} />
                                    </TwoColumn>
                                )}

                                <EmbedSnippet code={embedCode} />

                                {domains.length > 0 && (
                                    <CopyCodeButton onClick={handleClick}>{agentCodeCopied ? 'Copied!' : 'Copy embed code'}</CopyCodeButton>
                                )}
                                {agentCodeCopied && (
                                    <SuccessDiv>
                                        <IconSuccessCredits src={CheckCircleGreen} />
                                        <SuccessTextCredits>
                                            Great! Now paste the code snippet where you want your Agent to display.
                                        </SuccessTextCredits>
                                    </SuccessDiv>
                                )}
                            </StyledBoxPartionWithPadding>
                        </ContentContainer>
                    </LeftSettingsPanel>
                    <RightPreviewPanel>
                        <Preview position={position} mode={mode} />
                    </RightPreviewPanel>
                </Container>
            </ModalContent>
        </StyledDialog>
    );
});
